import { FC, useRef, useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import { toURL } from '@common/path';
import { stripAccolades } from '@common/string';
import { Recaptcha, RecaptchaRef } from '@components/Recaptcha/Recaptcha';
import { RecaptchaForm } from '@components/RecaptchaForm/RecaptchaForm';
import RichText from '@components/RichText/RichText';
import { useFormsPublicV2PostFormV2 } from '@dc/hooks';
import useDC from '@dc/useDC';
import { useRouter } from '@dxp-next';
import { RequestModels_Forms_V2_FormsRequestModelV2 } from '@monorepo-types/dc';
import { wrap } from '@sitecore/common';
import type { DeceasedFormRendering } from '@sitecore/types/DeceasedForm';
import { Button, Heading, Stack, Text, NotificationBox, Card, RadioButton, RadioGroup } from '@sparky';

import { ContactInputs } from './Components/ContactInputs';
import { DeceasedInputs } from './Components/DeceasedInputs';
import { getSchema } from './schema';
import type { FormValues } from './types';

export const DeceasedForm: FC<DeceasedFormRendering> = ({ fields }) => {
  const { contactFormFields, deceasedFormFields, errors: errorFields, footer, header, settings } = fields;

  const recaptchaRef = useRef<RecaptchaRef>(null);
  const { send, isSuccess, isError } = useFormsPublicV2PostFormV2();
  const { label, businessUnit } = useDC();
  const { pushHref } = useRouter();

  const resolver = yupResolver(getSchema(fields));
  const methods = useForm<FormValues>({
    mode: 'onBlur',
    resolver,
  });
  const {
    control,
    register,
    formState: { errors, isSubmitting },
  } = methods;

  const submitForm: SubmitHandler<FormValues> = values => {
    const {
      changePaymentMethod,
      contactEmail,
      contactFirstName,
      contactHouseNumber,
      contactHouseNumberSuffix,
      contactPhoneNumber,
      contactPostalCode,
      contactSalutation,
      contactSurname,
      contactSurnamePreposition,
      customerId,
      deceasedDate,
      deceasedHouseNumber,
      deceasedHouseNumberSuffix,
      deceasedPostalCode,
      endContractDate,
      recaptchaToken,
    } = values;
    const requestBody: RequestModels_Forms_V2_FormsRequestModelV2 = {
      data: {
        destinationCode: settings.senderIdOption.value,
        attachments: [],
        city: '',
        emailAddress: '',
        houseNumber: 0,
        houseNumberSuffix: '',
        postalCode: '',
        street: '',
        initials: '',
        surnamePreposition: '',
        surname: '',
        urlOrigin: window.location.href,
        formFields: [
          { key: 'Adressering contactpersoon', value: contactSalutation },
          { key: 'Voornaam contactpersoon', value: contactFirstName },
          { key: 'Tussenvoegsel contactpersoon', value: contactSurnamePreposition },
          { key: 'Achternaam contactpersoon', value: contactSurname },
          { key: 'Emailadres contactpersoon', value: contactEmail },
          { key: 'Telefoonnummer contactpersoon', value: contactPhoneNumber },
          { key: 'Postcode contactpersoon', value: contactPostalCode },
          { key: 'Huisnummer contactpersoon', value: contactHouseNumber },
          { key: 'Huisnummertoevoeging contactpersoon', value: contactHouseNumberSuffix },
          { key: 'Telefoonnummer contactpersoon', value: contactPhoneNumber },
          { key: 'Postcode contactpersoon', value: contactPostalCode },
          { key: 'Postcode overledene', value: deceasedPostalCode },
          { key: 'Huisnummer overledene', value: deceasedHouseNumber },
          { key: 'Huisnummertoevoeging overledene', value: deceasedHouseNumberSuffix },
          { key: 'Klantnummer', value: customerId },
          { key: 'Verander betaalmethode', value: changePaymentMethod },
          { key: 'Datum overlijden', value: deceasedDate },
          { key: 'Datum stopzetten', value: endContractDate },
        ],
      },
    };
    send({
      label,
      formId: stripAccolades(settings.senderIdOption.value),
      businessUnit,
      requestBody,
      recaptchaToken,
    });
  };

  useEffect(() => {
    if (!isSuccess) return;
    pushHref(toURL(footer.submitButtonLink.value.href));
  }, [isSuccess]);

  return (
    <Card>
      <Card.Content>
        <FormProvider {...methods}>
          <RecaptchaForm onSubmit={submitForm} recaptchaRef={recaptchaRef}>
            <Stack gap="6">
              <Heading as="h2" size="M">
                {wrap(header.title)}
              </Heading>
              <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
                {wrap(header.description, <RichText html={header.description.value} />)}
              </Text>
              <DeceasedInputs fields={deceasedFormFields} register={register} errors={errors} control={control} />
              <ContactInputs fields={contactFormFields} register={register} errors={errors} control={control} />
              <Heading as="h3" size="S">
                {wrap(footer.title)}
              </Heading>
              <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
                {wrap(footer.description, <RichText html={footer.description.value} />)}
              </Text>
              <Controller
                control={control}
                name="changePaymentMethod"
                render={({ field: { onChange, value, name } }) => (
                  <RadioGroup
                    name={name}
                    value={value}
                    onValueChange={onChange}
                    label={footer.changePaymentMethodFormField.value.label}
                    error={errors?.changePaymentMethod?.message}
                    hint={footer.changePaymentMethodFormField.value.hint}>
                    {footer.changePaymentMethodFormField.value.options?.map(({ label, value }) => (
                      <RadioButton value={value} key={value}>
                        {label}
                      </RadioButton>
                    ))}
                  </RadioGroup>
                )}
              />
              <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
                {wrap(footer.confirmDescription, <RichText html={footer.confirmDescription.value} />)}
              </Text>
              <Text size="BodyS">
                <Recaptcha hasError={!!errors.recaptchaToken} ref={recaptchaRef} />
              </Text>
              {isError ? (
                <NotificationBox
                  isAlert
                  text={<RichText html={errorFields.genericErrorNotification.value.content} />}
                  title={errorFields.genericErrorNotification.value.title}
                  variant={errorFields.genericErrorNotification.value.variant}
                />
              ) : null}
              <Stack.Item grow={false}>
                <Button type="submit" isLoading={isSubmitting}>
                  {footer.submitButtonLink.value?.text}
                </Button>
              </Stack.Item>
            </Stack>
          </RecaptchaForm>
        </FormProvider>
      </Card.Content>
    </Card>
  );
};

export default DeceasedForm;
